import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateTestModal, type CreatedAbTestPayload, type ScorecardPrefill, type SignalPrefill, type KeywordPrefill } from "@/components/ab-tests/CreateTestModal";
import type { ABTest, ABTestType, HeroAsinWithIntent } from "@/types/ab-tests";
import { productApi } from "@/resources/product";
import { useAppData } from "@/contexts/AppDataContext";
import { handleApiError } from "@/utils";
import { toast } from "@/hooks/use-toast";

export interface CreateABTestLocationState {
  heroAsin: HeroAsinWithIntent;
  productId: number;
  initialTestType?: ABTestType;
  initialTargetPhrase?: string;
  prefillFromScorecard?: ScorecardPrefill | null;
  prefillFromSignal?: SignalPrefill | null;
  prefillFromKeyword?: KeywordPrefill | null;
  prefillFromDraft?: ABTest | null;
  /** Where to navigate when the user cancels or finishes. Defaults to going back. */
  returnTo?: string;
  /** Default source to attribute to the created test (e.g. "scorecard", "keyword", "landscape"). */
  defaultSource?: string;
  /**
   * AUT-223 Section 6: set when the test is being started from an SEO Strategy
   * recommendation, so the created test links back to it by id rather than being
   * matched to it by text afterwards.
   */
  seoStrategyRecommendationId?: number;
}

const SESSION_KEY = "ab-tests:create:launch-context";

/**
 * Persist launch context into sessionStorage so it survives React strict-mode
 * double-mounts, lazy-chunk Suspense fallbacks, and hard refreshes.
 */
function readLaunchContext(routerState: unknown): CreateABTestLocationState | null {
  const fromRouter = (routerState ?? null) as CreateABTestLocationState | null;
  if (fromRouter && fromRouter.heroAsin) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(fromRouter));
    } catch {
      /* storage disabled */
    }
    return fromRouter;
  }
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw) as CreateABTestLocationState;
  } catch {
    /* ignore */
  }
  return null;
}

function clearLaunchContext() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export default function CreateABTest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBrandId } = useAppData();
  const createTestMutation = productApi.useCreateHeroAbTest();
  const updateTestMutation = productApi.useUpdateHeroAbTest();

  // Capture launch context ONCE on first render and hold it for the page
  // lifetime so transient router-state changes cannot unmount the modal.
  const initialStateRef = useRef<CreateABTestLocationState | null>(null);
  if (initialStateRef.current === null) {
    initialStateRef.current = readLaunchContext(location.state);
  }
  const ctx = initialStateRef.current;

  // AUT-430 (WEB-008), FR-15: undefined only for a resumed server draft, whose stored link the
  // page cannot read back (no A/B test GET serves it); otherwise the launch's strategy, or none.
  const initialStrategyLinkId = ctx?.prefillFromDraft ? undefined : (ctx?.seoStrategyRecommendationId ?? null);

  const goBack = useCallback(() => {
    clearLaunchContext();
    if (ctx?.returnTo) {
      navigate(ctx.returnTo);
    } else {
      navigate(-1);
    }
  }, [ctx?.returnTo, navigate]);

  useEffect(() => {
    if (!ctx?.heroAsin) {
      // No launch context (e.g. direct URL hit) — bounce back.
      clearLaunchContext();
      navigate("/ab-tests", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateTest = useCallback(
    async (payload: CreatedAbTestPayload) => {
      if (!ctx) return;
      const brandId = selectedBrandId;
      if (brandId == null) {
        toast({
          title: "Brand required",
          description: "Select a brand before creating a test.",
          variant: "destructive",
        });
        throw new Error("Brand required");
      }
      try {
        const draftId = ctx.prefillFromDraft?.id;

        if (draftId) {
          // Resuming an existing draft — update instead of creating a new test.
          // AUT-430 (WEB-008), FR-16: the link is three-valued on PATCH -- a number or `null` is
          // sent as given, and `undefined` ("as stored", the only value a resumed draft can carry)
          // must omit the key entirely rather than send it as `undefined`, which is why it is
          // spread in conditionally instead of always assigned.
          await updateTestMutation.mutateAsync({
            productId: ctx.productId,
            testId: draftId,
            body: {
              brand_id: brandId,
              scope_fields: payload.scopeFields,
              content_type: payload.contentType,
              intent_from: payload.intentFrom,
              intent_to: payload.intentTo,
              hypothesis: payload.hypothesis,
              target_phrase: payload.targetPhrase.trim() || undefined,
              status: payload.status ?? "running",
              source: payload.source ?? ctx.defaultSource,
              source_detail: payload.sourceDetail,
              ...(payload.seoStrategyRecommendationId !== undefined
                ? { seo_strategy_recommendation_id: payload.seoStrategyRecommendationId }
                : {}),
              variant_a: {
                title: payload.variantA.title,
                bullets: payload.variantA.bullets,
              },
              variant_b: {
                title: payload.variantB.title,
                bullets: payload.variantB.bullets,
              },
            },
          });
        } else {
          // New test — create. FR-16: POST always carries the link, the number or `null` (an
          // absent key and `null` are equivalent server-side, but the payload built by the modal
          // never carries `undefined` for a new test — only a resumed draft can).
          await createTestMutation.mutateAsync({
            productId: ctx.productId,
            body: {
              brand_id: brandId,
              name: payload.name,
              test_type: payload.testType,
              scope_fields: payload.scopeFields,
              content_type: payload.contentType,
              intent_from: payload.intentFrom,
              intent_to: payload.intentTo,
              hypothesis: payload.hypothesis,
              target_phrase: payload.targetPhrase.trim() || undefined,
              status: payload.status ?? "running",
              source: payload.source ?? ctx.defaultSource,
              source_detail: payload.sourceDetail,
              seo_strategy_recommendation_id: payload.seoStrategyRecommendationId ?? null,
              variant_a: {
                title: payload.variantA.title,
                bullets: payload.variantA.bullets,
              },
              variant_b: {
                title: payload.variantB.title,
                bullets: payload.variantB.bullets,
              },
            },
          });
        }
        if (payload.status !== "planning") {
          const desc =
            payload.intentFrom && payload.intentTo
              ? `A/B test "${payload.intentFrom} → ${payload.intentTo}" has been saved.`
              : `A/B test for "${payload.targetPhrase}" has been saved.`;
          toast({ title: draftId ? "Test updated" : "Test created", description: desc });
        }
      } catch (e) {
        handleApiError(e);
        throw e;
      }
    },
    [ctx, selectedBrandId, createTestMutation, updateTestMutation],
  );

  if (!ctx?.heroAsin) return null;

  return (
    <CreateTestModal
      fullPage
      heroAsin={ctx.heroAsin}
      onCreateTest={handleCreateTest}
      onCancel={goBack}
      initialTestType={ctx.initialTestType}
      initialTargetPhrase={ctx.initialTargetPhrase}
      prefillFromScorecard={ctx.prefillFromScorecard}
      prefillFromSignal={ctx.prefillFromSignal}
      prefillFromKeyword={ctx.prefillFromKeyword}
      prefillFromDraft={ctx.prefillFromDraft}
      initialStrategyLinkId={initialStrategyLinkId}
    />
  );
}
